'use client';

import React from 'react';
import Link from 'next/link';
import { ExternalLink } from 'lucide-react';
import type { AppraisalCycle } from '@/store/appraisalStore';

interface Props {
  cycles: AppraisalCycle[];
}

function pct(num: number, den: number) {
  if (!den) return 0;
  return Math.round((num / den) * 100);
}

function ProgressBar({ value, variant }: { value: number; variant: 'high' | 'mid' | 'low' | 'critical' }) {
  return (
    <div className="pn-appraisal__progress">
      <div className="pn-appraisal__progress-bar">
        <div
          className={`pn-appraisal__progress-bar-fill pn-appraisal__progress-bar-fill--${variant}`}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="pn-appraisal__progress-label">{value}%</span>
    </div>
  );
}

function getVariant(pct: number): 'high' | 'mid' | 'low' | 'critical' {
  if (pct >= 80) return 'high';
  if (pct >= 60) return 'mid';
  if (pct >= 40) return 'low';
  return 'critical';
}

const STATUS_CLASS: Record<string, string> = {
  active: 'pn-appraisal__status-badge--active',
  'in-progress': 'pn-appraisal__status-badge--in-progress',
  'under-review': 'pn-appraisal__status-badge--under-review',
  'hr-review': 'pn-appraisal__status-badge--hr-review',
  published: 'pn-appraisal__status-badge--published',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  'in-progress': 'In Progress',
  'under-review': 'Under Review',
  'hr-review': 'HR Review',
  published: 'Published',
};

export default function CycleHealthTable({ cycles }: Props) {
  return (
    <div className="pn-appraisal__table-wrapper">
      <div className="pn-appraisal__table-header">
        <div className="pn-appraisal__table-header-left">
          <span className="pn-appraisal__table-header-title">Active Cycle Health</span>
          <span className="pn-appraisal__table-header-count">{cycles.length} cycles</span>
        </div>
        <Link href="/appraisal-cycle-management" className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm">
          View All <ExternalLink size={12} />
        </Link>
      </div>
      <div className="pn-appraisal__table-scroll">
        <table className="pn-appraisal__review-table">
          <thead>
            <tr>
              <th>Cycle Name</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Submitted</th>
              <th>Mgr Reviewed</th>
              <th>HR Reviewed</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody>
            {cycles.map((c) => {
              const subPct = pct(c.submitted, c.totalAssigned);
              const mgrPct = pct(c.managerReviewed, c.submitted);
              const hrPct = pct(c.hrReviewed, c.managerReviewed);
              return (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--pn-text-primary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--pn-text-muted)', marginTop: 2 }}>{c.department}</div>
                  </td>
                  <td>
                    <span className={`pn-appraisal__status-badge ${STATUS_CLASS[c.status] || ''}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{c.totalAssigned}</span>
                  </td>
                  <td>
                    <ProgressBar value={subPct} variant={getVariant(subPct)} />
                  </td>
                  <td>
                    <ProgressBar value={mgrPct} variant={getVariant(mgrPct)} />
                  </td>
                  <td>
                    {c.hrReviewed > 0
                      ? <ProgressBar value={hrPct} variant={getVariant(hrPct)} />
                      : <span style={{ fontSize: 12, color: 'var(--pn-text-muted)' }}>—</span>
                    }
                  </td>
                  <td>
                    <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--pn-text-secondary)' }}>
                      {new Date(c.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}